import { equipmentBySlug } from "./equipment";
import { PUBLIC_SITE_ORIGIN } from "./seoCrawl";

export type PageSeo = {
  title: string;
  description: string;
  canonicalPath: string;
};

export const EQUIPMENT_SEO: PageSeo = {
  title: "Equipment catalogue — request a quote | EcoEng.net",
  description:
    "B2B equipment catalogue from Ecological Engineering. Request a quote for test systems, interconnect, modules, displays and industrial supply. No public prices and no cart.",
  canonicalPath: "/equipment",
};

export function equipmentSeoForSlug(slug: string): PageSeo {
  const product = equipmentBySlug(slug);
  if (!product) return EQUIPMENT_SEO;
  return {
    title: `${product.title} — request a quote | EcoEng.net`,
    description: `Request a B2B quote for ${product.title} from Ecological Engineering. Catalogue item only — no public price.`,
    canonicalPath: `/equipment/${product.slug}`,
  };
}

function normaliseSeoPath(pathname: string): string {
  if (!pathname || pathname === "/index.html") return "/";
  if (pathname.length > 1 && pathname.endsWith("/")) return pathname.slice(0, -1);
  return pathname;
}

export function seoForPath(pathname: string): PageSeo | null {
  const path = normaliseSeoPath(pathname);
  if (path === "/equipment") return EQUIPMENT_SEO;
  if (path.startsWith("/equipment/")) return equipmentSeoForSlug(path.slice("/equipment/".length));
  return null;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function replaceTitle(html: string, title: string): string {
  if (/<title>[^<]*<\/title>/i.test(html)) {
    return html.replace(/<title>[^<]*<\/title>/i, `<title>${title}</title>`);
  }
  return html.replace(/<\/head>/i, `    <title>${title}</title>\n  </head>`);
}

function upsertMetaByName(html: string, name: string, content: string): string {
  const re = new RegExp(`<meta\\s+[^>]*name=["']${name}["'][^>]*>`, "i");
  const tag = `<meta name="${name}" content="${escapeAttr(content)}" />`;
  if (re.test(html)) return html.replace(re, tag);
  return html.replace(/<\/head>/i, `    ${tag}\n  </head>`);
}

function upsertCanonical(html: string, href: string): string {
  const re = /<link\s+[^>]*rel=["']canonical["'][^>]*>/i;
  const tag = `<link rel="canonical" href="${escapeAttr(href)}" />`;
  if (re.test(html)) return html.replace(re, tag);
  return html.replace(/<\/head>/i, `    ${tag}\n  </head>`);
}

/** Crawler-visible head tags for the equipment lane. Other routes keep the SPA shell. */
export function applyRouteHtmlSeo(html: string, pathname: string): string {
  const seo = seoForPath(pathname);
  if (!seo) return html;
  const url =
    seo.canonicalPath === "/" ? `${PUBLIC_SITE_ORIGIN}/` : `${PUBLIC_SITE_ORIGIN}${seo.canonicalPath}`;
  let out = html;
  out = replaceTitle(out, seo.title);
  out = upsertMetaByName(out, "description", seo.description);
  out = upsertCanonical(out, url);
  return out;
}
